import { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { DataTable } from '../components/common/DataTable';
import { Calculator, Download, Calendar, DollarSign, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SalaryCalculationPage() {
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    totalGrossSalary: 0,
    totalDeductions: 0,
    totalNetSalary: 0,
  });

  const fetchSalaryData = useCallback(async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const response = await axios.get(`${API_URL}/api/salary/calculate?year=${year}&month=${month}`);
      setSalaryData(response.data.salaries || []);
      setSummary(response.data.summary || { totalEmployees: 0, totalGrossSalary: 0, totalDeductions: 0, totalNetSalary: 0 });
    } catch (error) {
      toast.error('Failed to calculate salaries');
      setSalaryData([]);
      setSummary({ totalEmployees: 1, totalGrossSalary: 0, totalDeductions: 0, totalNetSalary: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchSalaryData();
  }, [fetchSalaryData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const columns = [
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <div>
          <p className="font-medium text-white">{row.employee_name}</p>
          <p className="text-xs text-slate-500">{row.employee_id}</p>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (row) => <span className="text-slate-400">{row.department || '-'}</span>,
    },
    {
      key: 'working_days',
      header: 'Working Days',
      render: (row) => <span className="text-slate-300">{row.working_days || 0} / {row.total_days || 0}</span>,
    },
    {
      key: 'basic_salary',
      header: 'Basic',
      render: (row) => <span className="text-slate-300">{formatCurrency(row.basic_salary)}</span>,
    },
    {
      key: 'allowances',
      header: 'Allowances',
      render: (row) => <span className="text-green-400">{formatCurrency(row.total_allowances)}</span>,
    },
    {
      key: 'overtime_pay',
      header: 'Overtime',
      render: (row) => <span className="text-blue-400">{formatCurrency(row.overtime_pay)}</span>,
    },
    {
      key: 'gross_salary',
      header: 'Gross',
      render: (row) => <span className="text-white font-medium">{formatCurrency(row.gross_salary)}</span>,
    },
    {
      key: 'deductions',
      header: 'Deductions',
      render: (row) => <span className="text-red-400">-{formatCurrency(row.total_deductions)}</span>,
    },
    {
      key: 'net_salary',
      header: 'Net Salary',
      render: (row) => <span className="text-green-400 font-bold">{formatCurrency(row.net_salary)}</span>,
    },
  ];

  const handleExport = () => {
    toast.success('Exporting salary report...');
  };

  const handleProcess = () => {
    toast.success('Processing payroll...');
  };

  return (
    <div data-testid="salary-calculation-page">
      <PageHeader
        title="Salary Calculation"
        description="Calculate and manage monthly salaries"
        action={
          <div className="flex gap-2">
            <Button onClick={handleProcess} variant="outline" className="border-slate-700">
              <Calculator className="w-4 h-4 mr-2" />
              Process Payroll
            </Button>
            <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        }
      />

      {/* Month Selector */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-green-500/50"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Employees</p>
              <p className="text-xl font-bold text-white">{summary.totalEmployees}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Gross</p>
              <p className="text-xl font-bold text-white">{formatCurrency(summary.totalGrossSalary)}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Deductions</p>
              <p className="text-xl font-bold text-white">{formatCurrency(summary.totalDeductions)}</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Net</p>
              <p className="text-xl font-bold text-green-400">{formatCurrency(summary.totalNetSalary)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Salary Table */}
      <DataTable
        columns={columns}
        data={salaryData}
        loading={loading}
        emptyMessage="No salary data for selected month"
      />
    </div>
  );
}
